import React from "react";
import { shallowEqual } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { CircleMarker, Polyline, Popup, Tooltip, useMap } from "react-leaflet";
import { LatLngBoundsExpression, Map } from "leaflet";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useAppSelector } from "../../redux/hooks";
import { apiKey, apiPrefix, axiosHeaders, zoomRange } from "../../config";
import { AgencyContext } from "../../hooks/AgencyContext";
import {
  deviationCalculation,
  dynamicTextColor,
  getBusFillingColor,
  isNumeric,
  removeCoordinatesDuplicates,
} from "../../utils/helpers";
import { PopupContainer } from "../views/SelectedBusViewMap";
import { stopsOptionsConstant } from "../../data/constants";
import { getWheelColors } from "../../utils/helperWheelColors";
import variableColors from "../../_App.module.scss";

const { appBackgroundColor, darkColor } = variableColors;

export const TooltipContainer = styled.div`
  border: 1px solid ${darkColor};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
  div {
    padding: 0.25rem;
    &:nth-of-type(2) {
      border-left: 1px solid ${darkColor};
      border-right: 1px solid ${darkColor};
    }
  }
`;

//* The main component
const BlockWithTripsPunctualityAndOccupancyReportMap = (): JSX.Element => {
  const { isAuthenticated } = useAuth0() as { isAuthenticated: boolean };
  const leafletMap: Map = useMap();

  //* AgencyContext
  const { agencyId } = React.useContext(AgencyContext) as AgencyContextType;

  const [blockTripData, routesInfo]: [BlockTripData | null, RouteDetails[]] = useAppSelector(
    (state: RootState) => [state?.mapView?.blockTripData, state?.routesInfo?.routesInfo],
    shallowEqual
  );

  const [tripPoints, setTripPoints] = React.useState<[number, number][] | null>(null);
  const [tripData, setTripData] = React.useState<{ [key: string]: string } | null>(null);
  const [tripPunctualityAndOccupancyData, setTripPunctualityAndOccupancyData] = React.useState<TripReport[] | null>(null);
  // console.log("tripPunctualityAndOccupancyData:", tripPunctualityAndOccupancyData);

  React.useEffect(() => {
    if (!isAuthenticated || !leafletMap) return;

    if (isAuthenticated && blockTripData && routesInfo) {
      const { data: tripData, tripId } = blockTripData || {};

      setTripPunctualityAndOccupancyData(tripData);

      const URL = `${apiPrefix}/v1/key/${apiKey}/agency/${agencyId}/command/trip?tripId=${encodeURIComponent(tripId)}`;
      // console.log("URL:", URL);

      axios
        .get(URL, { headers: axiosHeaders })
        .then(({ data, status }) => {
          // console.log(data, status);
          if (status === 200) {
            const locations = data?.tripPattern?.stopPaths
              ?.map((elem: { locations: { lat: number; lon: number } }) => elem?.locations)
              ?.flat(1);
            // console.log("locations:", locations);

            //* Get locations
            const filteredLocations: Coordinate[] = removeCoordinatesDuplicates(locations, false);
            // console.log("filteredLocations:", filteredLocations);

            const pathPoints: [number, number][] = filteredLocations?.map((elem: Coordinate) => [
              elem?.lat,
              elem?.lon as number,
            ]);
            setTripPoints(pathPoints);

            //* Get extents
            const extents = data?.tripPattern?.extent;
            // console.log("extents:", extents);

            const bounds: [number, number][] = [
              [extents?.minLat - zoomRange * 1.5, extents?.minLon - zoomRange * 1.5],
              [extents?.maxLat + zoomRange * 1.5, extents?.maxLon + zoomRange * 1.5],
            ];
            // console.log("bounds:", bounds);
            leafletMap.fitBounds(bounds as LatLngBoundsExpression);

            //* Get trip/route data
            const routeId = data?.routeId as string;
            const routeShortName = data?.routeShortName as string;
            const directionId = data?.directionId as string;
            const headsign = data?.headsign as string;
            const serviceId = data?.serviceId as string;
            const tripId = data?.id as string;

            const currentRouteData = routesInfo?.find((route: RouteDetails) => route?.id === routeId) as RouteDetails;
            const color = currentRouteData?.color as string;
            const acronym = currentRouteData?.acronym || "";

            const tripData = {
              routeId,
              routeShortName,
              directionId,
              headsign,
              serviceId,
              tripId,
              color,
              acronym,
            };
            // console.log("tripData:", tripData);
            setTripData(tripData);
          }
        })
        .catch((error) => {
          if (axios.isAxiosError(error)) {
            console.log("error?.response?.status:", error?.response?.status);
          }
          if (error.response) {
            console.error("error.response.status:", error.response.status);
          }
        });
    } else {
      setTripPunctualityAndOccupancyData(null);
    }
  }, [agencyId, blockTripData, isAuthenticated, leafletMap, routesInfo]);

  return (
    <React.Fragment>
      {blockTripData && tripPoints && tripData && tripPunctualityAndOccupancyData ? (
        <React.Fragment>
          <Polyline
            positions={tripPoints}
            pathOptions={{ color: tripData?.color, weight: 4 }}
            children={<Popup>{<ContainerPopup tripData={tripData} />}</Popup>}
          />

          <TripStops
            tripPunctualityAndOccupancyData={tripPunctualityAndOccupancyData}
            color={tripData?.color || "#000000"}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default BlockWithTripsPunctualityAndOccupancyReportMap;

//* Polyline Popup
export const ContainerPopup = ({ tripData }: { tripData: { [key: string]: string } }): JSX.Element => {
  const { t } = useTranslation();

  return (
    <PopupContainer>
      <div className="popup-line">
        {t("Linia")}:{" "}
        <span
          style={{
            background: tripData?.color,
            color: dynamicTextColor(tripData?.color) || "#000000",
            padding: "0.25rem 0.5rem",
          }}
        >
          {tripData?.acronym || tripData?.routeShortName || tripData?.routeId || t("b/d")}
        </span>
      </div>
      <div className="popup-line">
        {t("Kurs")}: <span className="span_maroon"> {tripData?.tripId || t("b/d")}</span>
      </div>
      <div className="popup-line">
        {t("Dokąd")}: <span className="span_maroon"> {tripData?.headsign || t("b/d")}</span>
      </div>
      <div className="popup-line">
        {t("Kierunek")}: <span className="span_maroon">{tripData?.directionId || t("b/d")}</span>
      </div>
      <div className="popup-line">
        {t("Kalendarz")}: <span className="span_maroon">{tripData?.serviceId || t("b/d")}</span>
      </div>
    </PopupContainer>
  );
};

//* Trip Stops
const TripStops = ({
  tripPunctualityAndOccupancyData,
  color,
}: {
  tripPunctualityAndOccupancyData: TripReport[];
  color: string;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      {
        tripPunctualityAndOccupancyData?.map(
          (elem: TripReport, index: number, array: TripReport[]): JSX.Element => (
            <CircleMarker
              pathOptions={{
                ...stopsOptionsConstant,
                color: getWheelColors(color, 3)?.[1],
                fillColor: getWheelColors(color, 3)?.[2],
              }}
              key={String(index + elem?.stopid + elem?.stopname)}
              center={{
                lat: elem?.lat as number,
                lng: elem?.lon as number,
              }}
              radius={6}
            >
              <Tooltip interactive={true} permanent={true} direction={index % 2 === 0 ? "right" : "left"}>
                <TooltipContainer>
                  <div>
                    {t("Id")} <span className="span_maroon span_bold">{elem?.stopid}</span>{" "}
                    {`(${index + 1}/${array.length})`}
                  </div>
                  <div
                    className="span_bold"
                    style={{
                      backgroundColor:
                        !elem?.difference_in_seconds ||
                        deviationCalculation(elem?.departuretime, elem?.scheduledtime).colorDeviation === ""
                          ? appBackgroundColor
                          : deviationCalculation(elem?.departuretime, elem?.scheduledtime).colorDeviation,
                    }}
                  >
                    {!elem?.difference_in_seconds
                      ? t("b/d")
                      : deviationCalculation(elem?.departuretime, elem?.scheduledtime).stringDeviation}
                  </div>
                  <div
                    className="span_bold"
                    style={{
                      backgroundColor: getBusFillingColor(elem?.passenger_fullness),
                      textAlign: "center",
                    }}
                  >
                    {isNumeric(elem?.passenger_fullness) ? `${Number(elem?.passenger_fullness).toFixed(0)}%` : t("b/d")}
                  </div>
                </TooltipContainer>
              </Tooltip>
              <Popup>
                <PopupContainer>
                  <div className="popup-line">
                    {t("Nazwa")} <span className="span_maroon">{elem?.stopname}</span>
                  </div>
                  <div className="popup-line">
                    {t("Id")} <span className="span_maroon">{elem?.stopid}</span>
                  </div>
                  <div className="popup-line text-muted small">
                    {t("Pozycja")}: <span className="span_bold text-muted">{`${elem?.lat}; ${elem?.lon}`}</span>
                  </div>
                </PopupContainer>
              </Popup>
            </CircleMarker>
          )
        ) as JSX.Element[]
      }
    </React.Fragment>
  );
};
